/**
 * StreakCelebration — confetti + congratulations overlay shown when a
 * resident earns a 7/30/60-day milestone badge. Dismiss on tap.
 */
import { useMemo } from 'react';
import { Trophy, Medal } from 'lucide-react';

const GOLD = '#C9A962';
const COLORS = ['#C9A962', '#34d399', '#60a5fa', '#f472b6', '#fbbf24', '#f87171', '#a78bfa'];

export default function StreakCelebration({ badges, onDismiss }) {
  const pieces = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2.2,
    duration: 2.6 + Math.random() * 2,
    size: 6 + Math.random() * 8,
    color: COLORS[i % COLORS.length],
    rotate: Math.random() * 360,
    round: Math.random() > 0.5,
  })), []);

  if (!badges || badges.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center px-6"
      style={{ backgroundColor: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onDismiss} data-testid="streak-celebration-overlay">
      <style>{`
        @keyframes necs-confetti-fall {
          0% { transform: translateY(-8vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0.7; }
        }
        @keyframes necs-celebrate-pop {
          0% { transform: scale(0.7); opacity: 0; }
          70% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {pieces.map((p) => (
        <span key={p.id} className="pointer-events-none absolute top-0"
          style={{
            left: `${p.left}%`, width: p.size, height: p.size * (p.round ? 1 : 0.45),
            backgroundColor: p.color, borderRadius: p.round ? '50%' : '2px',
            transform: `rotate(${p.rotate}deg)`,
            animation: `necs-confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
          }} />
      ))}
      <div className="relative w-full max-w-sm rounded-3xl border p-6 text-center"
        style={{ borderColor: GOLD, background: 'linear-gradient(160deg,#1c1a13,#121212)', animation: 'necs-celebrate-pop 0.45s ease-out' }}
        data-testid="streak-celebration-card">
        <Trophy className="h-12 w-12 mx-auto mb-3" style={{ color: GOLD }} />
        <h2 className="text-2xl font-black uppercase tracking-wide text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Congratulations!
        </h2>
        <div className="mt-3 space-y-2">
          {badges.map((b, i) => (
            <div key={i} className="rounded-xl border border-zinc-700 bg-zinc-900/70 px-4 py-3 flex items-center gap-3"
              data-testid={`celebration-badge-${b.kind}-${b.days}`}>
              <Medal className="h-6 w-6 shrink-0" style={{ color: GOLD }} />
              <p className="text-sm text-left text-zinc-200">
                You earned the <span className="font-black text-white">{b.days}-Day Milestone Badge</span>{' '}
                on your <span className="font-black capitalize" style={{ color: b.kind === 'recycling' ? '#34d399' : GOLD }}>{b.kind}</span> streak!
              </p>
            </div>
          ))}
        </div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mt-5">Tap anywhere to continue</p>
      </div>
    </div>
  );
}
